/** Typed client for the Netlify Functions API. */

import type { GiantKill, RatingMove, UpsetCount, WinRateBoard } from './scoring/highlights';

export interface StandingRow {
  team_id: number;
  team_name: string;
  logo_url: string | null;
  /** House colour, used for a crest when there is no logo file. */
  colour: string | null;
  played: number;
  points: number;
  sets_for: number;
  sets_against: number;
  set_difference: number;
  games_for: number;
  games_against: number;
  game_difference: number;
  /** Position after the league's tiebreakers; shared when they cannot separate. */
  position?: number;
  /** True when only a playoff can separate this side from another. */
  needsPlayoff?: boolean;
  /** The season's minimum-games rule as it stands for this side. Null if none. */
  minGames?: MinGamesStanding | null;
}

/** How a side stands against the season's minimum-games rule. */
export interface MinGamesStanding {
  /** True once every league fixture has a result and the deduction has landed. */
  applied: boolean;
  /** Points deducted, as a positive number. Zero when the squad is on track. */
  deduction: number;
  /**
   * Of that, what can no longer be avoided — the part no amount of rotation in
   * the rounds left can fix. Nought all season for a squad that is on track,
   * where `deduction` is merely behind pro-rata and will catch up.
   */
  unavoidable: number;
  /** A line for a captain, phrased for whether it can still be fixed. */
  summary: string | null;
  /** Who is short, worst first. */
  short: { name: string; played: number; short: number }[];
  /** Who can no longer reach the minimum, worst first. `short` is by how much. */
  atRisk: { name: string; played: number; short: number }[];
}

export interface Season {
  id: number;
  name: string;
  leagueName: string;
  leagueSlug: string;
  /** Which league's visual identity to apply. */
  theme: string;
  /** Season badge, e.g. "Society Team League 2026". Carries the year. */
  logoUrl: string | null;
  /** The enduring league mark, used as a fallback and in the footer. */
  leagueLogoUrl: string | null;
  archived: boolean;
  scoringSystem: string;
  ratingCap: number | null;
  enforceRatingCap: boolean;
  startsOn?: string | null;
  endsOn?: string | null;
  /** Where the season is played. Null until a venue is set. */
  venueName?: string | null;
  /**
   * The Most Valued Player award's dates, and how many squads have named a
   * nominee. Optional so a cached page from before this shipped does not throw.
   *
   * The phase is derived from these with mvpState() rather than sent, so the
   * tab bar and the ballot cannot disagree about whether a vote is running.
   */
  mvp?: {
    nominationsClose: string | null;
    votingOpens: string | null;
    votingCloses: string | null;
    nominees: number;
  };
  /** Measured counts, sent by /api/leagues for the platform homepage. */
  stats?: SeasonStats;
}

/**
 * What a season actually contains, counted rather than recorded.
 *
 * `gameweeks` covers league fixtures only — knockout rounds occupy gameweeks of
 * their own and would otherwise inflate the season's apparent length.
 */
export interface SeasonStats {
  teams: number;
  players: number;
  matchesPlayed: number;
  gameweeks: number;
}

export interface PlayerStats {
  played: number;
  matchesWon: number;
  setsWon: number;
  gamesWon: number;
  /**
   * Dynamic Duo points, or NULL where the season has no figure for this player.
   *
   * Null rather than 0, because "no such statistic" and "a statistic that is
   * zero" are different claims and the table only hides a column on the first.
   * Season 1's figures were imported and are real; nothing has written one
   * since, and a DD point is won by a team rather than a player, so there is
   * nothing to derive. See teams.ts.
   */
  ddPoints: number | null;
  totalPoints: number;
  /** Points deducted, or null where the season has no figure. As ddPoints. */
  penalties: number | null;
}

/*
 * A season's highlights, as the engine in src/lib/scoring/highlights.ts derives
 * them. Re-exported here so a page imports one module rather than reaching into
 * the scoring library for a shape and the API client for the call.
 */
export type {
  GiantKill,
  RatedPlayer,
  RatingMove,
  UpsetCount,
  WinRate,
  WinRateBoard,
} from './scoring/highlights';

/** TrueRank as it stands, and how much evidence is behind it. */
export interface TrueRank {
  rating: number;
  /** Matches behind the rating: the carried seed plus this season's appearances. */
  matches: number;
  /** Under the threshold the rating is still part seed, part results, and moves fast. */
  provisional: boolean;
}

export interface Player {
  id: number;
  name: string;
  /**
   * The seeding rating, and the one the 6.15 pair cap is judged on.
   *
   * NOT the live rating. TrueRank can climb all season and never makes a legal
   * pairing illegal — the two are separate on purpose, and this is the half the
   * caps read.
   *
   * It used to say "fixed for the season", which was true of the rules as
   * written until the league removed the three-game-week window on committee
   * revisions. The committee may now revise a seed at any point, and the cap
   * follows it from that player's next game. What is still true is that it does
   * not drift: it changes only when somebody decides it does.
   */
  rating: number | null;
  /** The live rating, or null before the player has been rated. */
  trueRank: TrueRank | null;
  /**
   * League games against the season minimum. Null if the season has no minimum.
   *
   * `played` is the SQUAD PLACE's total, so for a player who took over
   * somebody's place it includes the games her predecessor played — the
   * minimum belongs to the place. `inherited` says how many of them are not
   * hers and `inheritedFrom` whose they are, so the table can show it rather
   * than appearing to credit her with games she did not play.
   */
  minGames: {
    required: number;
    played: number;
    short: number;
    exempt: boolean;
    inherited?: number;
    inheritedFrom?: string;
  } | null;
  /** Super tie-break record, from the pairings and the knockout deciders. */
  tieBreaks: { played: number; won: number; pointsDifference: number } | null;
  /** Squad tier, for leagues that use them. Null otherwise. */
  tier: string | null;
  isSubstitute: boolean;
  carriesForward: boolean;
  stats: PlayerStats | null;
}

export interface Team {
  id: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  /** House colour, for leagues with team colours but no logo file. */
  colour: string | null;
  /** Instagram handle without the @, when the side has given us one. */
  instagram: string | null;
  players: Player[];
}

export interface FixtureResult {
  matchId: number;
  scoringSystem: string;
  homePoints: number | null;
  awayPoints: number | null;
  homeSets: number | null;
  awaySets: number | null;
  homeGames: number | null;
  awayGames: number | null;
}

/** One side of a fixture, with whatever it has to identify itself by. */
export interface FixtureSide {
  id: number;
  name: string;
  logoUrl: string | null;
  colour: string | null;
  /** Instagram handle without the @, so a poster can tag the side. */
  instagram?: string | null;
}

export interface Fixture {
  id: number;
  gameweek: number;
  /** 'league' or a knockout round. */
  stage: string;
  scheduledAt: string | null;
  court: string | null;
  status: string;
  home: FixtureSide;
  away: FixtureSide;
  result: FixtureResult | null;
  /**
   * The pairs declared at the toss, empty until they are.
   *
   * Carried on the fixture so the list can show who is playing whom without a
   * call per fixture.
   */
  lineups?: {
    slot: number;
    homePlayers: string[];
    awayPlayers: string[];
    /**
     * The home pair's predicted share of the games, 0 to 1, from the two pairs'
     * ratings as they stand. Null where a pairing is half named, or where the
     * season holds no rating for one of the four.
     *
     * A PREDICTION, not the figure the ledger records afterwards — see odds.ts.
     * Shown only while a fixture is unplayed, which is also the only time the
     * pairs appear on this list at all.
     */
    predictedHome?: number | null;
  }[];
  /**
   * The six each side named, for the leagues that run team sheets.
   *
   * `null` where the league does not run them at all, which is how a fixture
   * says "this question does not apply to me" rather than "nobody has
   * submitted". `players` is empty while the embargo holds — the server does
   * not send names that are not yet public, so there is nothing here to hide
   * in the browser and nothing in the network tab either.
   */
  teamSheets?: FixtureTeamSheets | null;
}

export interface FixtureTeamSheets {
  /** Whether the six are public to everyone: the hour AND both sheets in. */
  revealed: boolean;
  /** When they become public. Null for a fixture nobody has dated. */
  revealAt: string | null;
  home: { submitted: boolean; players: string[] };
  away: { submitted: boolean; players: string[] };
}

export interface PairDetail {
  slot: number;
  legacy: boolean;
  homePlayers: string[];
  awayPlayers: string[];
  homePlayerIds: number[];
  awayPlayerIds: number[];
  homeLabel: string | null;
  awayLabel: string | null;
  /** 'H' or 'A' where a side retired part-way through. See scoring/types.ts. */
  retiredBy?: 'H' | 'A' | null;
  sets: { home: number | null; away: number | null }[];
  tiebreak: { home: number | null; away: number | null };
  legacySetsWon: { home: number | null; away: number | null };
  isDdHome: boolean;
  isDdAway: boolean;
  base: { home: number | null; away: number | null };
  forfeitedBy: string | null;
  /**
   * Why: 'declared' — a side could not field players, which costs three
   * points; 'cap' — the pair was over the rating cap, which costs only the
   * pairing. Null where the pairing was played. See migration 150.
   */
  forfeitKind: 'declared' | 'cap' | null;
  /** Rain stopped play and it was not resumed: only the completed sets count. */
  stopped: boolean;
  pairMatchupId: number;
  /** What this pairing did to the four players' ratings. Null if it was not rated. */
  ratings: PairRatings | null;
}

/** One side's team sheet, and whether they were late with it. */
export interface TeamSheet {
  /**
   * The six, or EMPTY while the sheet is still embargoed.
   *
   * Emptied by the server rather than omitted, so the shape is the same before
   * and after 10am and only the contents change. Read `revealed` to tell an
   * embargoed sheet from a side who named nobody.
   */
  playerIds: number[];
  players: string[];
  /** The sheet is in. True even while the names are held. */
  submitted: boolean;
  /** Whether the names above are the real ones. False before the fixture's 10am. */
  revealed: boolean;
  lateSubmission: boolean;
  lateToToss: boolean;
  submittedAt: string | null;
}

/** Who has the toss, and who may name whose first pair. */
export interface TossOutcome {
  wonBy: 'home' | 'away' | null;
  /** True when it was lost by rule rather than to a coin. */
  forfeited: boolean;
  /** Sides whose first pair the opposition chooses. */
  firstPairChosenFor: ('home' | 'away')[];
  reasons: string[];
}

/** The super tie-break that settled a level knockout tie. */
export interface Decider {
  homePlayers: string[];
  awayPlayers: string[];
  /** The same players by id, so a scoring screen can load the tie-break back. */
  homePlayerIds: number[];
  awayPlayerIds: number[];
  home: number;
  away: number;
  wonBy: 'home' | 'away';
}

/** One pairing's rating impact, as the ledger recorded it. */
export interface PairRatings {
  /** The home pair's chance of winning, from the ratings before they played. */
  expectedHome: number;
  /** What the scoreline said, on the same 0-1 scale. */
  scoreHome: number;
  players: {
    name: string;
    side: 'home' | 'away';
    before: number;
    after: number;
    delta: number;
    k: number;
    matchesBefore: number;
    provisional: boolean;
  }[];
}

export interface MatchDetail {
  fixture: {
    id: number;
    seasonId: number;
    seasonName: string;
    archived: boolean;
    /** The season's engine, known before the fixture has been played. */
    scoringSystem: string;
    gameweek: number;
    /** 'league', or a knockout round. Decides the team sheet size and game count. */
    stage: string;
    scheduledAt: string | null;
    court: string | null;
    status: string;
    home: { id: number; name: string };
    away: { id: number; name: string };
    /** Which side won the toss, if a line-up has been submitted. */
    tossWonBy: 'home' | 'away' | null;
    /** Which side the toss winner sent in first. The night alternates from there. */
    firstDownBy: 'home' | 'away' | null;
    /** Where this season is played, for a match-day poster. Null until set. */
    venue?: { name: string | null; instagram: string | null } | null;
    /** When this season's rating rules were frozen. Null if it has none of its own. */
    ratingRulesLockedAt?: string | null;
    /** When those rules were corrected, if they ever were, and what was wrong. */
    ratingRulesCorrectedAt?: string | null;
    ratingRulesCorrection?: string | null;
  };
  match: {
    id: number;
    legacy: boolean;
    /** Which engine scored it: 'vitalli', 'season2' or 'legacy_season1'. */
    scoringSystem: string;
    homePoints: number | null;
    awayPoints: number | null;
    homeBase: number | null;
    awayBase: number | null;
    gamesBonusTo: string | null;
    tiebreakBonusTo: string | null;
    homeDdBonus: number | null;
    awayDdBonus: number | null;
    homeSets: number | null;
    awaySets: number | null;
    homeGames: number | null;
    awayGames: number | null;
    homePenalty: number | null;
    awayPenalty: number | null;
    notes: string | null;
    playedAt: string | null;
  } | null;
  pairs: PairDetail[];
  /**
   * Pairings each side has already used against this opponent this season. A
   * pair may play together only once across the two meetings.
   */
  /**
   * Prior LEAGUE pairings this season for each side, across every opponent —
   * what Society's "maximum of 3 league rounds per pairing" is counted from.
   */
  pairRounds?: {
    home: { playerIds: [number, number]; gameweek: number }[];
    away: { playerIds: [number, number]; gameweek: number }[];
  };
  priorPairs?: {
    home: { playerIds: number[]; names: string[]; gameweek: number }[];
    away: { playerIds: number[]; names: string[]; gameweek: number }[];
  };
  /** Pairs as declared at the toss, which exist before any result does. */
  lineups: {
    slot: number;
    homePlayerIds: (number | null)[];
    awayPlayerIds: (number | null)[];
    homePlayers: string[];
    awayPlayers: string[];
    /** The home pair's predicted share of the games. See Fixture.lineups. */
    predictedHome?: number | null;
  }[];
  /** The super tie-break that settled a level knockout tie, if one was played. */
  decider: Decider | null;
  /** The team sheets and what being late with them cost. */
  teamSheets: {
    home: TeamSheet | null;
    away: TeamSheet | null;
    /** When the six become public — 10am on the matchday. Null if undated. */
    revealAt: string | null;
    /**
     * Whether the six are public. The hour AND both sheets being in — one side
     * withholding theirs holds the other side's back too, so that a captain who
     * misses the deadline cannot read the opposition before naming their own.
     *
     * Note this is the PUBLIC answer: an organiser reading the same response
     * sees the names regardless, so a screen must read the sheet's own
     * `revealed` to know what it is holding.
     */
    revealed: boolean;
    /** Whether 10am has passed, whether or not that revealed anything. */
    deadlinePassed: boolean;
    toss: TossOutcome;
  };
}

/** A side as a night reports it: enough to draw it and enough to tag it. */
export interface NightSide {
  id: number;
  name: string;
  colour: string | null;
  logoUrl: string | null;
  /** Instagram handle without the @, when the side has given us one. */
  instagram: string | null;
}

/** One match night in full — every fixture of a gameweek and every game in it. */
export interface NightDetail {
  season: string;
  seasonId: number;
  archived: boolean;
  scoringSystem: string;
  gameweek: number;
  label: string;
  gameweeks: {
    gameweek: number;
    /** 'league', or the knockout round — semi_final, final, fifth_place… */
    stage?: string;
    label: string;
    /** What the picker chip shows: the match day's number, or SF, Final, 5th. */
    short?: string;
    fixtures: number;
    played: number;
    scheduledAt: string | null;
  }[];
  fixtures: {
    id: number;
    court: string | null;
    scheduledAt: string | null;
    status: string;
    home: NightSide;
    away: NightSide;
    result: {
      homePoints: number | null;
      awayPoints: number | null;
      homeSets: number | null;
      awaySets: number | null;
      homeGames: number | null;
      awayGames: number | null;
    } | null;
    games: {
      slot: number;
      homePlayers: string[];
      awayPlayers: string[];
      sets: { home: number | null; away: number | null }[];
      tiebreak: { home: number | null; away: number | null };
      points: { home: number | null; away: number | null };
      /**
       * Which side nominated this pairing as its Dynamic Duo. The DD point is
       * earned by a PAIRING and added to the team total, so a nominated pair
       * that sweeps is worth four off the night rather than three.
       */
      isDdHome: boolean;
      isDdAway: boolean;
      /** The odds TrueRank gave the home pair, and what the result said. Null
          when the pairing was never rated. Society's Pair of the Week uses these. */
      expectedHome: number | null;
      scoreHome: number | null;
      forfeitedBy: 'home' | 'away' | null;
      /** The side that went off injured part-way through, if any. */
      retiredBy: 'home' | 'away' | null;
    }[];
  }[];
}

export interface Sponsor {
  id: number;
  name: string;
  logoUrl: string | null;
  websiteUrl: string | null;
  /** 1 is the headline tier; everything defaults to 1 until tiers are decided. */
  tier: number;
  /** The sponsor's own line. Null until they supply one. */
  tagline: string | null;
  /** Instagram handle without the @. Null means shown on the graphic but not tagged. */
  instagram?: string | null;
  /**
   * What this sponsor presents, e.g. "Pair of the Week". Null for an ordinary
   * sponsor.
   *
   * A presenter is billed on that award every time it is given — on the poster,
   * in the caption and on the Night tab. Whether they ALSO hold a plaque slot
   * is a separate question, answered by `inRotation`.
   */
  presents?: string | null;
  /**
   * Whether they take a slot in the rotating plaque.
   *
   * Its own fact rather than inferred from `presents`. The two were the same
   * decision once — a presenter was billed on the award INSTEAD of the plaque —
   * and then The Bed Shop bought both, at which point inferring it would have
   * meant dropping their billing to give them a slot.
   */
  inRotation?: boolean;
  /**
   * The mark drawn for a DARK ground — white or knocked-out ink.
   *
   * The plaque is a white panel because sponsor artwork is drawn for paper. The
   * award panel is the exception, sitting on the card's navy, so a sponsor
   * billed there needs the reverse of the mark the plaque uses. Null means
   * `logoUrl` serves everywhere.
   */
  logoUrlReversed?: string | null;
}

/** One food stall on a match night, as /api/fixtures serves it. */
/**
 * A stall as the FIXTURES payload carries it: everything but the artwork.
 *
 * The logo is deliberately absent — see netlify/functions/fixtures.ts. It is
 * fetched from /api/vendors only when a poster is being built.
 */
export interface NightVendorRow {
  id: number;
  name: string;
  tagline: string | null;
  /** Bare handle, no @. Null where the vendor has none, which must stay untagged. */
  instagram: string | null;
}

/** The same stall with its logo, as /api/vendors serves it for a poster. */
export interface NightVendorArt extends NightVendorRow {
  logoUrl: string | null;
}

export interface League {
  id: number;
  name: string;
  slug: string;
  /** Which visual identity to apply inside this league. */
  theme: string;
  logoUrl: string | null;
  /** What kind of competition it is, e.g. "Team League". */
  category: string | null;
  /** Where it is played, e.g. "South Africa". */
  region: string | null;
  /** One-sentence introduction, shown on the platform homepage. */
  tagline: string | null;
  /** The league's own Instagram handle, without the @. */
  instagram: string | null;
  /** How this league plays off sides its tiebreakers cannot separate. */
  playoffFormat: string | null;
  /** What this league calls its sides — Teams, Houses, Clubs. */
  unitNoun: string;
  /** Singular of the above, for labels. */
  unitNounSingular: string;
  /** Carries 8Ball's own sponsors, because this league is hosted for nothing. */
  showsPlatformSponsors: boolean;
  /** Whether this league has a post-match summary of each night. */
  showsPostMatch: boolean;
  /**
   * Whether this league holds a Most Valued Player ballot.
   *
   * Optional so a cached page from before this shipped does not throw — and it
   * fails CLOSED, because an undefined flag reading as "runs the award" would
   * put the thing back on the three leagues this was added to take it off.
   */
  runsMvp?: boolean;
  /**
   * The intake this league is taking registrations for, as the form should
   * word it. Null or absent means it is not taking any.
   */
  interestIntake?: string | null;
  /** The season this league is taking squad entries for (Season 3), or null. */
  squadIntake?: string | null;
  /** When those entries close (ISO), or null. */
  squadDeadline?: string | null;
  /** When current sides must answer on their captain's link (ISO), or null. */
  returnDeadline?: string | null;
  /** When that season is played, as written: "March – April 2027". */
  squadSeasonRuns?: string | null;
  /** That season's badge, for its entry cards. */
  squadLogoUrl?: string | null;
  /** Whether pairs are limited by a combined rating. Tiered leagues are not. */
  usesRatingCap: boolean;
  /** How players are graded: 'rating', 'tier' or 'none'. */
  playerGrading: string;
  /**
   * The league's own domain, when it has one, as a bare host.
   *
   * On it the platform disappears: that address opens the league itself rather
   * than the list of leagues, and no other league is reachable. Null for a
   * league reached only through the platform.
   */
  hostname: string | null;
  /** Whether this league has an admin password configured. */
  adminConfigured: boolean;
  seasons: Season[];
}

/*
 * The admin password, kept per league.
 *
 * It used to be one value under one key for the whole platform, which was true
 * enough when there was one league. With four run by four different people it
 * meant unlocking Premier Pearls and then opening Norwood sent Pearls'
 * password to Norwood's endpoint, which refused it — correctly — with the word
 * "Unauthorised" and no hint that the password belonged to somebody else's
 * league. The screen still showed every control, because a password was
 * stored; it was simply the wrong one.
 *
 * Per league, an organiser who has not unlocked this one is told so up front
 * instead of finding out when a match night's result will not save.
 *
 * The old key is deliberately not carried over. It cannot be known which
 * league it was for, and guessing would recreate the bug it is here to fix —
 * so everyone unlocks once more, per league, and then it holds.
 */
let currentLeague = '';

/** Which league the stored password belongs to. Set by App as the page routes. */
export const setAdminLeague = (slug: string): void => {
  currentLeague = slug;
};

const adminKey = (): string => `admin-password:${currentLeague || 'platform'}`;

export const getAdminPassword = (): string | null => localStorage.getItem(adminKey());
export const setAdminPassword = (value: string): void => localStorage.setItem(adminKey(), value);
export const clearAdminPassword = (): void => localStorage.removeItem(adminKey());

/**
 * Visit counts, as `/api/visits` reports them.
 *
 * `visits` are distinct browsing sessions and `views` are page opens; a session
 * that looks at four sections is one visit and four views. The two are counted
 * separately rather than one estimated from the other, because the number that
 * goes in front of a sponsor has to survive being asked how it was arrived at.
 */
export interface VisitReport {
  /** Johannesburg's today, so a series can be bucketed against the right day. */
  today: string;
  days: { day: string; visits: number; views: number }[];
  leagues: { slug: string; name: string; visits: number; views: number }[];
  /** The platform's own landing page, which belongs to no league. Null when asking about one. */
  platformPages: { visits: number; views: number } | null;
  sections: { section: string; visits: number }[];
}

/**
 * One player's season, as `/api/player` assembles it.
 *
 * SEASON-SCOPED and PUBLIC. Nothing here is new information — it is on the
 * standings, the squad tables and the fixture pages already — but it is the
 * first place a player can see their own season in one piece.
 */
export interface PlayerAppearanceRow {
  fixtureId: number;
  gameweek: number;
  stage: string;
  scheduledAt: string | null;
  court: string | null;
  slot: number;
  side: 'home' | 'away';
  /** The other player on this player's own side; null for a half-named pair. */
  partner: string | null;
  opponents: string[];
  opponentTeam: string;
  points: { mine: number | null; theirs: number | null };
  /** Null while undecided or unscored — a level pairing is not a loss. */
  won: boolean | null;
  wasDd: boolean;
  retired: boolean;
  forfeited: boolean;
  /** Their side declared the forfeit — they did not come. Counts as nothing. */
  didNotPlay: boolean;
  sets: { mine: number | null; theirs: number | null }[];
  tiebreak: { mine: number | null; theirs: number | null };
  /** What this pairing did to their TrueRank. Null where the season rates nothing. */
  rating: { before: number; after: number; delta: number } | null;
}

export interface PlayerSeason {
  season: string;
  seasonId: number;
  archived: boolean;
  player: {
    id: number;
    name: string;
    team: {
      id: number;
      name: string;
      slug: string;
      logoUrl: string | null;
      colour: string | null;
    } | null;
    isSubstitute: boolean;
    tier: string | null;
    seed: number | null;
    trueRank: TrueRank | null;
    stats: PlayerStats | null;
    tieBreaks: { played: number; won: number; pointsDifference: number } | null;
    /**
     * `unreachable` is nought while a player can still reach the minimum, which
     * is the difference between this and the squad table's `short`. See the
     * comment in netlify/functions/player.ts.
     */
    minGames: {
      required: number;
      played: number;
      unreachable: number;
      exempt: boolean;
      roundsLeft: number;
    } | null;
  };
  matches: PlayerAppearanceRow[];
  /** Seed corrections, with the reason migration 112 records on each. */
  seedChanges: { from: number | null; to: number | null; at: string; note: string | null }[];
}

/**
 * The Most Valued Player ballot, as /api/mvp serves it.
 *
 * No count and nobody's choices: a running tally would tell a late voter which
 * way to push. The organiser reads those from /api/mvp-votes.
 */
export interface MvpBallot {
  season: string;
  seasonId: number;
  archived: boolean;
  /** Whether the ballot accepts votes right now. Derived from the deadlines. */
  open: boolean;
  /** Which round the award is in: none, nominating, before, voting, closed. */
  phase: MvpPhase;
  /** When squads must have named a nominee. ISO, or null for no date set. */
  nominationsClose: string | null;
  votingOpens: string | null;
  votingCloses: string | null;
  nominees: MvpNominee[];
  /** Everybody on a squad sheet this season, for the voter to find her name. */
  voters: { playerId: number; name: string; teamId: number | null }[];
  /** Voters who already have a ballot in. Ids only, never their choices. */
  votedPlayerIds: number[];
}

export type MvpPhase = 'none' | 'nominating' | 'before' | 'voting' | 'closed';

export interface MvpNominee {
  playerId: number;
  name: string;
  teamId: number;
  teamName: string;
}

/** The count and every ballot. Behind the organiser's password. */
/** One person who has asked to join a coming season. Organiser-only. */
export interface InterestRegistration {
  id: number;
  fullName: string;
  cell: string;
  dateOfBirth: string | null;
  playtomicName: string | null;
  playtomicLevel: number | null;
  ownRanking: string | null;
  side: string | null;
  accolades: string | null;
  status: string;
  notes: string | null;
  submittedAt: string;
  /** Derived from the declared level on every read — never stored. */
  suggested: { tier: string | null; because: string; needsReview: boolean };
  /** Whether her own grading matches the suggestion. Null if either is absent. */
  agreesWithSelf: boolean | null;
}

/** Somebody who has asked about buying a team. Organiser-only. */
export interface OwnerRegistration {
  id: number;
  fullName: string;
  cell: string;
  email: string;
  area: string | null;
  wherePlays: string | null;
  willPlay: boolean;
  status: string;
  notes: string | null;
  submittedAt: string;
}

/** A squad an owner has proposed for a coming season. Organiser-only. */
export interface SquadEntry {
  id: number;
  /** A current side answering through its captain's link, or a new team. */
  returning: boolean;
  response: 'new' | 'same' | 'changes' | 'out';
  teamName: string;
  ownerName: string | null;
  cell: string | null;
  email: string | null;
  /** `today`: the player's rating now — TrueRank for a returning player. */
  /** `playtomic` is null for a returning player, who is not asked for one. */
  players: { fullName: string; playtomic: number | null; rating: number; playerId?: number; today: number; stars: number }[];
  /** The total when the squad was sent. */
  ratingTotal: number;
  /** The same squad on today's TrueRank — it moves until Season 2 ends. */
  todayTotal: number;
  /** The squad's stars on those ratings — what the limit of 23 is read on. */
  stars: number;
  status: string;
  notes: string | null;
  submittedAt: string;
}

export interface MvpVotes {
  season: string;
  seasonId: number;
  open: boolean;
  phase: MvpPhase;
  nominationsClose: string | null;
  votingOpens: string | null;
  votingCloses: string | null;
  nominees: MvpNominee[];
  standings: {
    playerId: number;
    name: string;
    teamName: string;
    points: number;
    firsts: number;
    seconds: number;
    thirds: number;
  }[];
  votes: {
    voter: string;
    voterTeam: string | null;
    first: string;
    second: string;
    third: string;
    reason: string | null;
    castAt: string;
  }[];
  /** Who has not voted yet, so the organiser knows who to chase. */
  notVoted: string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const password = getAdminPassword();
  if (password) headers.set('x-admin-password', password);
  if (init?.body) headers.set('content-type', 'application/json');

  const response = await fetch(path, { ...init, headers });
  const text = await response.text();

  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Unexpected response from ${path}`);
  }

  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new Error(message);
  }

  return body as T;
}

const seasonQuery = (seasonId?: number) => (seasonId ? `?season=${seasonId}` : '');

type LeaguesResponse = {
  platformAdminConfigured: boolean;
  leagues: League[];
  /** The engines a new season may be scored by, as the platform has them. */
  scoringSystems: { name: string; label: string }[];
};

/*
 * ONE request for the league list, however many screens ask in the same
 * moment. The app shell and the platform homepage both need it on the first
 * paint and each asked for its own — two identical requests on every launch,
 * on the connection the page was waiting on. Shared while in flight and for a
 * few seconds after, then asked afresh, so an organiser's edit still shows on
 * the next screen.
 */
let leaguesInFlight: { at: number; promise: Promise<LeaguesResponse> } | null = null;
const LEAGUES_SHARED_MS = 5_000;

export const api = {
  leagues: (): Promise<LeaguesResponse> => {
    if (leaguesInFlight && Date.now() - leaguesInFlight.at < LEAGUES_SHARED_MS) return leaguesInFlight.promise;
    const promise = request<LeaguesResponse>('/api/leagues');
    leaguesInFlight = { at: Date.now(), promise };
    // A failure is not kept for others to inherit.
    promise.catch(() => {
      if (leaguesInFlight?.promise === promise) leaguesInFlight = null;
    });
    return promise;
  },

  seasons: () => request<{ adminConfigured: boolean; seasons: Season[] }>('/api/seasons'),

  standings: (seasonId?: number) =>
    request<{
      season: string;
      seasonId: number;
      archived: boolean;
      tiebreakers: string[];
      /** True once every league fixture has a result. */
      seasonComplete?: boolean;
      /** The season's minimum-games rule, or null if it has none. */
      minGames?: { minGames: number; penaltyPerGame: number } | null;
      standings: StandingRow[];
    }>(`/api/standings${seasonQuery(seasonId)}`),

  teams: (seasonId?: number) =>
    request<{
      season: string;
      seasonId: number;
      archived: boolean;
      /** The season's minimum-games rule, or null if it has none. */
      minGames?: { minGames: number; penaltyPerGame: number } | null;
      /**
       * True when a seeding rating has moved since the ratings were last
       * replayed, so the stored movements still start from the old figure.
       *
       * Optional, so a cached page from before this shipped does not throw.
       */
      ratingsStale?: boolean;
      teams: Team[];
      /** Substitutes the league supplies, who belong to no team. */
      substitutes?: Player[];
    }>(`/api/teams${seasonQuery(seasonId)}`),

  fixtures: (seasonId?: number) =>
    request<{
      season: string;
      seasonId: number;
      archived: boolean;
      /** Where this season is played, for a match-day poster. Null until set. */
      venue: { name: string | null; instagram: string | null } | null;
      gameweeks: {
        gameweek: number;
        label: string;
        stage: string;
        fixtures: Fixture[];
        /** Food stalls booked for this match night. Empty for a knockout round. */
        vendors: NightVendorRow[];
      }[];
    }>(`/api/fixtures${seasonQuery(seasonId)}`),

  /*
   * Fetched only when a poster is being built, never on a page load: these
   * are photographs and a league's set is a couple of megabytes. See
   * netlify/functions/backgrounds.ts.
   */
  /*
   * One night's stalls WITH their logos, fetched only when a poster is being
   * built. The logos are data URLs and do not belong on a page load.
   */
  nightVendors: (seasonId: number, gameweek: number) =>
    request<{ vendors: NightVendorArt[] }>(
      `/api/vendors?season=${seasonId}&gameweek=${gameweek}`,
    ),

  backgrounds: (league: string) =>
    request<{ backgrounds: { id: number; name: string; imageUrl: string }[] }>(
      `/api/backgrounds?league=${encodeURIComponent(league)}`,
    ),

  sponsors: (seasonId?: number) =>
    request<{
      sponsors: Sponsor[];
      /** Platform size, sent only when asking for the platform's own sponsors. */
      reach?: { players: number; leagues: number };
    }>(`/api/sponsors${seasonQuery(seasonId)}`),

  highlights: (seasonId?: number) =>
    request<{
      season: string;
      seasonId: number;
      archived: boolean;
      /** Whether the gaps are measured in live TrueRank or in the season's seeds. */
      ratingsFrom: 'truerank' | 'seed';
      giantKillers: GiantKill[];
      upsetLeaderboard: UpsetCount[];
      /** Null until a season has moved a rating at all. */
      movers: {
        gameweek: number;
        /** The round's own name, since a knockout's gameweek is only an ordering device. */
        stage?: string;
        risers: RatingMove[];
        fallers: RatingMove[];
      } | null;
      /**
       * Win rate for every player who has played, best first.
       *
       * Optional so a browser holding a cached page from before this shipped
       * does not throw on a response that has no such field — the component
       * treats a missing board the same as an empty one.
       */
      winRates?: WinRateBoard;
    }>(`/api/highlights${seasonQuery(seasonId)}`),

  match: (fixtureId: number) => request<MatchDetail>(`/api/match?fixtureId=${fixtureId}`),

  /** The ballot: nominees and the roll of voters. Public, and countless. */
  /** Whether a league is taking names for a coming season, and what to call it. */
  interest: (league: string) =>
    request<{ league: string; slug: string; open: boolean; intake: string | null }>(
      `/api/interest?league=${encodeURIComponent(league)}`,
    ),

  /** Register interest. Public — anyone may send one, nobody may read them. */
  registerInterest: (payload: {
    league: string;
    fullName: string;
    cell: string;
    dateOfBirth?: string | null;
    playtomicName?: string | null;
    playtomicLevel?: number | null;
    ownRanking?: string | null;
    side?: string | null;
    accolades?: string | null;
  }) =>
    request<{ ok: true; intake: string; groupLink: string | null }>('/api/interest', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** The organiser's list. Behind the league password; carries personal data. */
  interestList: (league: string) =>
    request<{
      league: string;
      intake: string | null;
      /** The league's WhatsApp group invite, if it has one. */
      groupLink: string | null;
      registrations: InterestRegistration[];
    }>(`/api/interest?league=${encodeURIComponent(league)}&list=1`),

  /** Whether a league is taking names from would-be team owners. */
  ownerInterest: (league: string) =>
    request<{ league: string; slug: string; open: boolean; intake: string | null }>(
      `/api/owner-interest?league=${encodeURIComponent(league)}`,
    ),

  /** Register interest in buying a team. Public — anyone may send one, nobody may read them. */
  registerOwnerInterest: (payload: {
    league: string;
    fullName: string;
    cell: string;
    email: string;
    area?: string | null;
    wherePlays?: string | null;
    willPlay: boolean;
  }) =>
    request<{ ok: true; intake: string }>('/api/owner-interest', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** The organiser's list of would-be owners. Behind the league password. */
  ownerInterestList: (league: string) =>
    request<{
      league: string;
      intake: string | null;
      owners: OwnerRegistration[];
    }>(`/api/owner-interest?league=${encodeURIComponent(league)}&list=1`),

  /** Whether a league is taking squad entries for a coming season. */
  squadEntryStatus: (league: string) =>
    request<{ league: string; slug: string; open: boolean; intake: string | null; deadline: string | null; closed: boolean; size: number; limit: number }>(
      `/api/squad-entry?league=${encodeURIComponent(league)}`,
    ),

  /** Submit a squad. Public — anyone may send one, nobody may read them. */
  submitSquadEntry: (payload: {
    league: string;
    teamName: string;
    ownerName: string;
    cell: string;
    email?: string | null;
    players: { fullName: string; playtomic: number; rating: number }[];
  }) =>
    request<{ ok: true; intake: string; total: number; stars: number; over: boolean }>('/api/squad-entry', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** The organiser's list of squad entries. Behind the league password. */
  squadEntryList: (league: string) =>
    request<{ league: string; intake: string | null; deadline: string | null; returnDeadline: string | null; limit: number; notAnswered: string[]; notInvited: string[]; entries: SquadEntry[] }>(
      `/api/squad-entry?league=${encodeURIComponent(league)}&list=1`,
    ),

  mvp: (seasonId?: number) => request<MvpBallot>(`/api/mvp${seasonQuery(seasonId)}`),

  /** Cast or change a ballot. A second one from the same voter replaces it. */
  castMvpVote: (payload: {
    seasonId: number;
    voterPlayerId: number;
    firstPlayerId: number;
    secondPlayerId: number;
    thirdPlayerId: number;
    reason?: string | null;
  }) => request<{ ok: true }>('/api/mvp', { method: 'POST', body: JSON.stringify(payload) }),

  /** The count, the ballots and who has not voted. Organiser only. */
  mvpVotes: (seasonId?: number) => request<MvpVotes>(`/api/mvp-votes${seasonQuery(seasonId)}`),

  /**
   * Name the nominees, or set either round's deadline. Organiser only.
   *
   * A key left out is untouched; a key sent as null clears that date. So
   * saving nominees does not wipe the deadlines, and clearing a deadline is a
   * thing you can actually do.
   */
  saveMvpSetup: (payload: {
    seasonId: number;
    nominees?: { teamId: number; playerId: number }[];
    nominationsClose?: string | null;
    votingOpens?: string | null;
    votingCloses?: string | null;
  }) => request<{ ok: true }>('/api/mvp-votes', { method: 'POST', body: JSON.stringify(payload) }),

  /** One player's season. Public — no password rides on this. */
  player: (playerId: number, seasonId?: number) =>
    request<PlayerSeason>(
      `/api/player?id=${playerId}${seasonId ? `&season=${seasonId}` : ''}`,
    ),

  night: (seasonId?: number, gameweek?: number) =>
    request<NightDetail>(
      `/api/night${seasonQuery(seasonId)}${
        gameweek ? `${seasonId ? '&' : '?'}gameweek=${gameweek}` : ''
      }`,
    ),

  saveMatch: (payload: unknown) =>
    request<{
      ok: true;
      matchId: number;
      total: { home: number; away: number };
      /** Anything the server wants the organiser to know — an over-cap walkover,
          a pairing past its three league rounds. Worth reading, not discarding. */
      notes?: string[];
    }>('/api/save-match', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * What the visit counting has gathered. Behind a password: a league's
   * organiser sees their own, the platform password sees every league.
   *
   * Note this goes through `request`, which attaches the password — unlike the
   * recording side in `track.ts`, which must not.
   */
  visits: (leagueSlug?: string) =>
    request<VisitReport>(`/api/visits${leagueSlug ? `?league=${encodeURIComponent(leagueSlug)}` : ''}`),

  /** Admin unlock is per league, so the league being unlocked must be named. */
  verifyAdmin: (leagueSlug: string) =>
    request<{ ok: true }>(`/api/admin-check?league=${encodeURIComponent(leagueSlug)}`, {
      method: 'POST',
      body: '{}',
    }),
};
